import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Alert,
    Pagination,
    Stack,
    Dialog,
    DialogContent,
    IconButton,
    Button
} from '@mui/material';
import { Search, Eye, X, CreditCard, ArrowRight } from 'lucide-react';
import { getPaymentTransactions, getPaymentTransactionById } from '../../Services/Bookings/BookingsApis';
import { useNavigate, useLocation } from 'react-router-dom';
import GlobalPagination from '../../Components/Common/Globalpagination';

// ─── Design tokens ────────────────────────────────────────────────────────────
const GOLD = '#FFBD65';

const StatusBadge = ({ status }) => {
    let bgcolor = '#F1F5F9';
    let color = '#475569';

    if (status === 'completed') {
        bgcolor = '#E8F5E8';
        color = '#2E7D32';
    } else if (status === 'pending') {
        bgcolor = '#FFF8E1';
        color = '#B45309';
    } else if (status === 'failed') {
        bgcolor = '#FFEBEE';
        color = '#C62828';
    }

    return (
        <Box
            sx={{
                display: 'inline-block',
                px: 1.25,
                py: 0.35,
                borderRadius: '4px',
                bgcolor,
                color,
                fontSize: '0.7rem',
                fontWeight: 500,
                textTransform: 'capitalize'
            }}
        >
            {status || '—'}
        </Box>
    );
};

const PaymentTransactionDetails = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        bookingId: '',
        status: '',
        gateway: '',
        page: 1,
        pageSize: 25,
    });
    const [pagination, setPagination] = useState({
        totalRecords: 0,
        totalPages: 0,
        currentPage: 1,
        pageSize: 25,
    });

    const [openModal, setOpenModal] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);
    const [modalError, setModalError] = useState(null);

    // ─── Helpers ──────────────────────────────────────────────────────────────
    const debounce = (func, delay) => {
        let timeoutId;
        return (...args) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => func.apply(null, args), delay);
        };
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    // ─── Data fetching ────────────────────────────────────────────────────────
    const fetchTransactions = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getPaymentTransactions(filters);
            setTransactions(response.data.transactions || []);
            setPagination({
                totalRecords: response.data.totalRecords || 0,
                totalPages: response.data.totalPages || 1,
                currentPage: response.data.currentPage || 1,
                pageSize: response.data.pageSize || 10,
            });
        } catch (err) {
            setError(err.message || 'Failed to fetch payment transactions');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    const debouncedFetch = useMemo(() => debounce(fetchTransactions, 500), [fetchTransactions]);

    useEffect(() => {
        debouncedFetch();
    }, [debouncedFetch]);

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({
            ...prev,
            [field]: value,
            page: field === 'page' ? value : 1,
        }));
    };

    const handleViewDetails = useCallback(async (id) => {
        setOpenModal(true);
        setModalLoading(true);
        setModalError(null);
        setSelectedTransaction(null);
        try {
            const response = await getPaymentTransactionById(id);
            if (response.success && response.data) {
                setSelectedTransaction(response.data);
            } else {
                setModalError('Failed to retrieve transaction details');
            }
        } catch (err) {
            setModalError(err.response?.data?.message || 'Error retrieving transaction');
        } finally {
            setModalLoading(false);
        }
    }, []);

    useEffect(() => {
        if (location.state?.transactionId) {
            handleViewDetails(location.state.transactionId);
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, location.pathname, navigate, handleViewDetails]);

    const handleCloseModal = () => setOpenModal(false);

    // ─── Shared input sx ──────────────────────────────────────────────────────
    const inputSx = {
        bgcolor: '#FFFFFF',
        '& .MuiOutlinedInput-root': {
            borderRadius: '6px',
            height: '38px',
            fontSize: '0.8rem',
            '& fieldset': { borderColor: '#E2E8F0' },
            '&:hover fieldset': { borderColor: '#CBD5E1' },
            '&.Mui-focused fieldset': { borderColor: GOLD, borderWidth: '1px' },
        },
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, minHeight: '100vh' }}>

            {/* ── Header ──────────────────────────────────────────────────── */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Typography sx={{ fontSize: { xs: '1.25rem', md: '1.4rem' }, fontWeight: 500, color: '#1E293B' }}>
                    Payment Transactions
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        placeholder="Search booking ID"
                        variant="outlined"
                        size="small"
                        value={filters.bookingId}
                        onChange={(e) => handleFilterChange('bookingId', e.target.value)}
                        sx={{ width: { xs: '100%', sm: 190 }, ...inputSx }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search size={16} color="#64748B" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel sx={{ fontSize: '0.8rem', color: '#64748B', '&.Mui-focused': { color: GOLD } }}>Status</InputLabel>
                        <Select
                            value={filters.status}
                            label="Status"
                            onChange={(e) => handleFilterChange('status', e.target.value)}
                            sx={{
                                borderRadius: '6px',
                                height: '38px',
                                bgcolor: '#FFFFFF',
                                fontSize: '0.8rem',
                                '& fieldset': { borderColor: '#E2E8F0' },
                                '&:hover fieldset': { borderColor: '#CBD5E1' },
                                '&.Mui-focused fieldset': { borderColor: GOLD, borderWidth: '1px' },
                            }}
                        >
                            <MenuItem value="" sx={{ fontSize: '0.8rem' }}>All</MenuItem>
                            <MenuItem value="pending" sx={{ fontSize: '0.8rem' }}>Pending</MenuItem>
                            <MenuItem value="completed" sx={{ fontSize: '0.8rem' }}>Completed</MenuItem>
                            <MenuItem value="failed" sx={{ fontSize: '0.8rem' }}>Failed</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel sx={{ fontSize: '0.8rem', color: '#64748B', '&.Mui-focused': { color: GOLD } }}>Gateway</InputLabel>
                        <Select
                            value={filters.gateway}
                            label="Gateway"
                            onChange={(e) => handleFilterChange('gateway', e.target.value)}
                            sx={{
                                borderRadius: '6px',
                                height: '38px',
                                bgcolor: '#FFFFFF',
                                fontSize: '0.8rem',
                                '& fieldset': { borderColor: '#E2E8F0' },
                                '&:hover fieldset': { borderColor: '#CBD5E1' },
                                '&.Mui-focused fieldset': { borderColor: GOLD, borderWidth: '1px' },
                            }}
                        >
                            <MenuItem value="" sx={{ fontSize: '0.8rem' }}>All</MenuItem>
                            <MenuItem value="razorpay" sx={{ fontSize: '0.8rem' }}>Razorpay</MenuItem>
                            <MenuItem value="stripe" sx={{ fontSize: '0.8rem' }}>Stripe</MenuItem>
                            <MenuItem value="paypal" sx={{ fontSize: '0.8rem' }}>PayPal</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            {/* ── Error ───────────────────────────────────────────────────── */}
            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '6px' }}>{error}</Alert>}

            {/* ── Table ───────────────────────────────────────────────────── */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress size={24} sx={{ color: GOLD }} />
                </Box>
            ) : (
                <Paper sx={{ borderRadius: '6px', border: '1px solid #E2E8F0', overflow: 'hidden', bgcolor: '#FFFFFF' }} elevation={0}>
                    <TableContainer>
                        <Table sx={{ width: '100%', minWidth: 1050, tableLayout: 'auto' }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                                    {['Booking ID', 'Gateway', 'Amount', 'Currency', 'Status', 'Created By', 'Updated By', 'Created At', 'Updated At', 'Actions'].map((head) => (
                                         <TableCell
                                             key={head}
                                             align={head === 'Actions' ? 'right' : 'left'}
                                             sx={{
                                                 fontWeight: 600,
                                                 color: '#475569',
                                                 fontSize: '0.7rem',
                                                 textTransform: 'uppercase',
                                                 letterSpacing: '0.05em',
                                                 py: 1.5,
                                                 px: 1.5,
                                                 borderBottom: '1px solid #E2E8F0',
                                                 whiteSpace: 'nowrap',
                                                 ...(head === 'Created By' || head === 'Updated By' ? { width: '130px' } : {}),
                                                 ...(head === 'Created At' || head === 'Updated At' ? { width: '120px' } : {})
                                             }}
                                         >
                                             {head}
                                         </TableCell>
                                     ))}
                                 </TableRow>
                            </TableHead>
                            <TableBody>
                                {transactions.length > 0 ? (
                                    transactions.map((transaction) => (
                                        <TableRow
                                            key={transaction.id}
                                            sx={{
                                                '&:last-child td': { borderBottom: 0 },
                                            }}
                                        >
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap' }}>
                                                <Typography
                                                    onClick={() => navigate('/bookings/view-details', { state: { booking: transaction.bookingId } })}
                                                    sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#1976D2', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                                >
                                                    #{transaction.bookingId}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap' }}>
                                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '4px', px: 1, py: 0.25 }}>
                                                    <CreditCard size={12} color="#64748B" />
                                                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 500, color: '#475569', textTransform: 'capitalize' }}>
                                                        {transaction.gateway}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', fontWeight: 500, color: '#1E293B', whiteSpace: 'nowrap' }}>
                                                {transaction.amount}
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, color: '#64748B', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                                                {transaction.currency}
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap' }}>
                                                <StatusBadge status={transaction.status} />
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, color: '#64748B', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                                                {transaction.cuName || '-'}
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, color: '#64748B', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                                                {transaction.uuName || '-'}
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, color: '#64748B', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                                                {formatDate(transaction.createdAt)}
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, color: '#64748B', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                                                {formatDate(transaction.updatedAt)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap' }}>
                                                <IconButton
                                                    size="small"
                                                    disableFocusRipple disableRipple disableTouchRipple
                                                    onClick={() => handleViewDetails(transaction.id)}
                                                    sx={{
                                                        color: '#64748B',
                                                        p: 0.5,
                                                        '&:hover': { color: '#1E293B' }
                                                    }}
                                                >
                                                    <Eye size={16} />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={10} sx={{ textAlign: 'center', py: 6, color: '#64748B', fontSize: '0.75rem' }}>
                                            No payment transactions found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {/* ── Pagination ───────────────────────────────────────────────── */}
            <GlobalPagination
                page={filters.page}
                pageSize={filters.pageSize}
                totalRecords={pagination.totalRecords}
                totalPages={pagination.totalPages}
                onPageChange={(newPage) => {
                    handleFilterChange('page', newPage);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                onPageSizeChange={(newSize) => {
                    handleFilterChange('pageSize', newSize);
                }}
                loading={loading}
            />

            {/* ── Modal ────────────────────────────────────────────────────── */}
            <Dialog
                open={openModal}
                onClose={handleCloseModal}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '8px',
                        border: '1px solid #E2E8F0',
                        boxShadow: 'none',
                        bgcolor: '#FFFFFF',
                        maxWidth: 460,
                    },
                }}
            >
                {/* Modal Header */}
                <Box
                    sx={{
                        px: 2.5,
                        pt: 2.5,
                        pb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: '1px solid #F1F5F9',
                    }}
                >
                    <Box>
                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 500, color: '#1E293B', lineHeight: 1.3 }}>
                            Transaction Details
                        </Typography>
                        {selectedTransaction && (
                            <Typography sx={{ fontSize: '0.75rem', color: '#64748B', mt: 0.25 }}>
                                Booking #{selectedTransaction.bookingId}
                            </Typography>
                        )}
                    </Box>
                    <IconButton
                        onClick={handleCloseModal}
                        size="small"
                        disableFocusRipple disableRipple disableTouchRipple
                        sx={{
                            color: '#64748B',
                            p: 0.5,
                            '&:hover': { color: '#1E293B' }
                        }}
                    >
                        <X size={16} />
                    </IconButton>
                </Box>

                {/* Modal Body */}
                <DialogContent sx={{ p: 2.5 }}>
                    {modalLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                            <CircularProgress size={24} sx={{ color: GOLD }} />
                        </Box>
                    ) : modalError ? (
                        <Alert severity="error" sx={{ borderRadius: '6px' }}>{modalError}</Alert>
                    ) : selectedTransaction ? (
                        <Stack spacing={2}>
                            {/* Amount banner */}
                            <Box
                                sx={{
                                    bgcolor: '#F8FAFC',
                                    border: '1px solid #E2E8F0',
                                    borderRadius: '6px',
                                    px: 2,
                                    py: 1.75,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Box>
                                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.35 }}>
                                        Amount Paid
                                    </Typography>
                                    <Typography sx={{ fontSize: '1.25rem', fontWeight: 500, color: '#1E293B', lineHeight: 1 }}>
                                        {selectedTransaction.currency} {selectedTransaction.amount}
                                    </Typography>
                                </Box>
                                <StatusBadge status={selectedTransaction.status} />
                            </Box>

                            {/* Key-Value Details Card */}
                            <Box
                                sx={{
                                    border: '1px solid #E2E8F0',
                                    borderRadius: '6px',
                                    overflow: 'hidden',
                                    bgcolor: '#FFFFFF',
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.25, borderBottom: '1px solid #F1F5F9' }}>
                                    <Typography sx={{ fontSize: '0.75rem', color: '#64748B', fontWeight: 400 }}>Booking ID</Typography>
                                    <Typography
                                        onClick={() => {
                                            handleCloseModal();
                                            navigate('/bookings/view-details', { state: { booking: selectedTransaction.bookingId } });
                                        }}
                                        sx={{ fontSize: '0.75rem', color: '#1976D2', fontWeight: 500, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                    >
                                        #{selectedTransaction.bookingId}
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.25, borderBottom: '1px solid #F1F5F9' }}>
                                    <Typography sx={{ fontSize: '0.75rem', color: '#64748B', fontWeight: 400 }}>Payment Gateway</Typography>
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '4px', px: 1, py: 0.25 }}>
                                        <CreditCard size={11} color="#64748B" />
                                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 500, color: '#475569', textTransform: 'capitalize' }}>
                                            {selectedTransaction.gateway}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.25, borderBottom: '1px solid #F1F5F9' }}>
                                    <Typography sx={{ fontSize: '0.75rem', color: '#64748B', fontWeight: 400 }}>Currency</Typography>
                                    <Typography sx={{ fontSize: '0.75rem', color: '#1E293B', fontWeight: 500 }}>{selectedTransaction.currency}</Typography>
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.25, borderBottom: (selectedTransaction.razorpayOrderId || selectedTransaction.paypalOrderId || selectedTransaction.razorpayPaymentId || selectedTransaction.paypalPaymentId) ? '1px solid #F1F5F9' : 'none' }}>
                                    <Typography sx={{ fontSize: '0.75rem', color: '#64748B', fontWeight: 400 }}>Date & Time</Typography>
                                    <Typography sx={{ fontSize: '0.75rem', color: '#1E293B', fontWeight: 500 }}>{formatDate(selectedTransaction.createdAt)}</Typography>
                                </Box>

                                {(selectedTransaction.razorpayOrderId || selectedTransaction.paypalOrderId) && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.25, borderBottom: (selectedTransaction.razorpayPaymentId || selectedTransaction.paypalPaymentId) ? '1px solid #F1F5F9' : 'none' }}>
                                        <Typography sx={{ fontSize: '0.75rem', color: '#64748B', fontWeight: 400 }}>Order ID</Typography>
                                        <Typography sx={{ fontSize: '0.72rem', color: '#475569', fontWeight: 500, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                            {selectedTransaction.razorpayOrderId || selectedTransaction.paypalOrderId}
                                        </Typography>
                                    </Box>
                                )}

                                {(selectedTransaction.razorpayPaymentId || selectedTransaction.paypalPaymentId) && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.25 }}>
                                        <Typography sx={{ fontSize: '0.75rem', color: '#64748B', fontWeight: 400 }}>Payment ID</Typography>
                                        <Typography sx={{ fontSize: '0.72rem', color: '#475569', fontWeight: 500, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                            {selectedTransaction.razorpayPaymentId || selectedTransaction.paypalPaymentId}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Stack>
                    ) : null}
                </DialogContent>

                {/* Modal Footer */}
                {!modalLoading && !modalError && selectedTransaction && (
                    <Box
                        sx={{
                            px: 2.5,
                            py: 2,
                            borderTop: '1px solid #F1F5F9',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 1.5,
                        }}
                    >
                        <Button
                            variant="outlined"
                            disableElevation disableFocusRipple disableRipple
                            onClick={handleCloseModal}
                            sx={{
                                borderColor: '#E2E8F0',
                                borderRadius: '6px',
                                fontSize: '0.8rem',
                                fontWeight: 500,
                                color: '#475569',
                                textTransform: 'none',
                                px: 2.5,
                                py: 0.75,
                                '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' }
                            }}
                        >
                            Close
                        </Button>
                        <Button
                            variant="contained"
                            disableElevation disableFocusRipple disableRipple
                            onClick={() => {
                                handleCloseModal();
                                navigate('/bookings/view-details', { state: { booking: selectedTransaction.bookingId } });
                            }}
                            endIcon={<ArrowRight size={15} />}
                            sx={{
                                bgcolor: GOLD,
                                color: '#000000',
                                borderRadius: '6px',
                                fontSize: '0.8rem',
                                fontWeight: 500,
                                textTransform: 'none',
                                boxShadow: 'none',
                                px: 2.5,
                                py: 0.75,
                            }}
                        >
                            View Booking
                        </Button>
                    </Box>
                )}
            </Dialog>
        </Box>
    );
};

export default PaymentTransactionDetails;
